import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Environment, EnvironmentService } from '@galaxy/core';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { filter, first, map, shareReplay, switchMap } from 'rxjs/operators';
import { URL_REGEX } from '../../../common/form/validator';
import {
  IdentityProviderConfig,
  IdentityProviderConfigService,
  OAuth2IdentityProviderConfig,
  OpenIDIdentityProviderConfig,
} from '../identity-provider-config.service';
import { DeleteDialogComponent } from './delete-dialog.component';

const callbackRoute = '/oauth2/callback';

interface FormValues {
  integrationType: 'oauth2' | 'openid';
  clientId: string;
  clientSecret: string;
  openid: OpenIDIdentityProviderConfig;
  oauth2: OAuth2IdentityProviderConfig;
}

@Component({
  selector: 'app-identity-provider-config-page',
  templateUrl: './identity-provider-config-page.component.html',
  styleUrls: ['./identity-provider-config-page.component.scss'],
  standalone: false,
})
export class IdentityProviderConfigPageComponent implements OnInit {
  readonly urlPattern = 'https?://.+';
  redirectUri: string;

  readonly form = new UntypedFormGroup({
    integrationType: new UntypedFormControl('openid'),
    clientId: new UntypedFormControl(''),
    clientSecret: new UntypedFormControl(''),
    openid: new UntypedFormGroup({
      discoveryEndpoint: new UntypedFormControl(''),
    }),
    oauth2: new UntypedFormGroup({
      authorizationEndpoint: new UntypedFormControl(''),
      tokenEndpoint: new UntypedFormControl(''),
      userinfoEndpoint: new UntypedFormControl(''),
    }),
  });

  private readonly loading$$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  protected readonly loading$: Observable<boolean> = this.loading$$.asObservable();
  private readonly error$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  protected readonly error$: Observable<boolean> = this.error$$.asObservable();
  private readonly saving$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  protected readonly saving$: Observable<boolean> = this.saving$$.asObservable();
  private existingConfig$$ = new BehaviorSubject<FormValues | null>(null);
  protected readonly existingConfig$: Observable<FormValues> = this.existingConfig$$.asObservable();

  constructor(
    private readonly idpConfigService: IdentityProviderConfigService,
    private readonly snackbarService: SnackbarService,
    private readonly dialog: MatDialog,
    private readonly environmentSerivce: EnvironmentService,
    private readonly _: GalaxyAiIconService,
  ) {}

  ngOnInit(): void {
    this.redirectUri =
      this.environmentSerivce.getEnvironment() === Environment.PROD
        ? `https://sso-api-prod.apigateway.co${callbackRoute}`
        : `https://sso-api-demo.apigateway.co${callbackRoute}`;

    const config$ = this.idpConfigService.getIdentityProviderConfig().pipe(shareReplay(1));
    this.existingConfig$
      .pipe(
        filter((x) => !!x),
        first(),
      )
      .subscribe((newValue) => this.form.setValue(newValue));
    config$
      .pipe(
        filter((x) => !!x),
        map((config: IdentityProviderConfig) => {
          const newValue: FormValues = {
            integrationType: config.integrationType,
            clientId: config.clientId,
            clientSecret: config.clientSecret || '',
            oauth2: {
              userinfoEndpoint: '',
              tokenEndpoint: '',
              authorizationEndpoint: '',
            },
            openid: { discoveryEndpoint: '' },
          };

          switch (config.integrationType) {
            case 'oauth2':
              newValue.oauth2 = config.oauth2;
              break;

            case 'openid':
              newValue.openid = config.openid;
              break;
          }
          return newValue;
        }),
      )
      .subscribe({
        next: (newConfig) => {
          this.existingConfig$$.next(newConfig);
          this.loading$$.next(false);
        },
        error: (err) => {
          if (err && err.status !== 404) {
            this.error$$.next(true);
          }
          this.loading$$.next(false);
        },
      });
  }

  validateForm(hasExistingConfig: boolean): string[] {
    // I'm using a custom function here rather than reactive form validators
    // because it was way more annoying than it should have been to get validators
    // dynamically adding/removing when switching integration types while still
    // properly setting input fields as required in the view layer.
    // This solution properly checks whether the required fields are filled in,
    // while properly displaying a "required" indicator in the view.

    const getField = <T>(field: string) => this.form.get(field).value as T;
    const isEmpty = (field: string) => !getField(field);
    const isUrl = (field: string) => URL_REGEX.test(getField<string>(field));

    const oauth2Validations: string[] = [
      ...(!isUrl('oauth2.authorizationEndpoint') ? ['Authorization Endpoint must be a URL'] : []),
      ...(!isUrl('oauth2.tokenEndpoint') ? ['Token Endpoint must be a URL'] : []),
      ...(!isUrl('oauth2.userinfoEndpoint') ? ['Userinfo Endpoint must be a URL'] : []),
    ];
    const openidValidations: string[] = [
      ...(!isUrl('openid.discoveryEndpoint') ? ['Discovery Endpoint must be a URL'] : []),
    ];

    const validations: string[] = [
      ...(isEmpty('clientId') ? ['Client ID is required'] : []),
      ...(!hasExistingConfig && isEmpty('clientSecret') ? ['Client Secret is required'] : []),
      ...(getField('integrationType') === 'oauth2' ? oauth2Validations : []),
      ...(getField('integrationType') === 'openid' ? openidValidations : []),
    ];
    return validations;
  }

  onSubmit(): void {
    this.existingConfig$
      .pipe(
        first(),
        switchMap((existingConfig) => {
          const formErrors = this.validateForm(!!existingConfig);
          if (formErrors.length > 0) {
            // Only alert for a single error at a time
            this.snackbarService.openErrorSnack(formErrors.join(', '));
            return EMPTY;
          } else {
            this.saving$$.next(true);
            const newConfig = this.form.value;
            if (existingConfig === null) {
              // No existing config, we need to create one
              return this.idpConfigService.createIdentityProviderConfig(newConfig).pipe(map(() => newConfig));
            } else {
              // Existing config, we need to update it

              // If the client secret hasn't been updated, use the existing one.
              if (!newConfig.clientSecret) {
                newConfig.clientSecret = existingConfig.clientSecret;
              }
              return this.idpConfigService.replaceIdentityProviderConfig(newConfig).pipe(map(() => newConfig));
            }
          }
        }),
      )
      .subscribe({
        next: (savedConfig) => {
          this.existingConfig$$.next(savedConfig);
          this.saving$$.next(false);
          this.snackbarService.openSuccessSnack('Integration configured');
        },
        error: (errResp: HttpErrorResponse) => {
          this.saving$$.next(false);
          this.snackbarService.openErrorSnack(errResp.error.message);
        },
      });
  }

  openDeleteDialog(): void {
    const ref = this.dialog.open(DeleteDialogComponent, { maxWidth: '650px' });
    ref.afterClosed().subscribe((result) => {
      if (result) {
        this.existingConfig$$.next(null);
        this.form.setValue({
          integrationType: 'openid',
          clientId: '',
          clientSecret: '',
          oauth2: {
            userinfoEndpoint: '',
            tokenEndpoint: '',
            authorizationEndpoint: '',
          },
          openid: { discoveryEndpoint: '' },
        });
        this.snackbarService.openSuccessSnack('Integration deleted');
      }
    });
  }
}
